<template>
    <div class="omocom-optional-insurance" :class="modifiers">
        <div v-if="isPopular" class="pill pill--tertiary-lightest pill--disabled">
            <strong>
                {{ $t('pages.booking_request.popular_choice') }}
            </strong>
        </div>

        <label v-if="useCheckbox" class="checkbox-label omocom-optional-insurance__checkbox-label">
            <input v-model="reduceDeductible" type="checkbox" class="checkbox" @change="onChange" />
            <span class="checkbox__text omocom-optional-insurance__title">
                {{ $t('pages.booking_request.insurance_step.complete_reduce_deductible') }}
            </span>
        </label>
        <div v-else class="omocom-optional-insurance__wrapper">
            <p class="omocom-optional-insurance__title">
                {{ insuranceDetails.title }}
            </p>
            <svg-icon v-if="isStatusAdded" name="check" />
        </div>

        <div class="omocom-optional-insurance__description">
            <p v-if="insuranceDetails.infoText">{{ insuranceDetails.infoText }}</p>
            <p v-if="insuranceDetails.extraInfoText">
                {{ insuranceDetails.extraInfoText }}
            </p>
        </div>

        <i18n
            v-if="insuranceDetails.price"
            class="omocom-optional-insurance__price"
            path="pricing.x_per_night"
            tag="div"
        >
            <template #price>
                <strong>{{ insuranceDetails.price }}</strong>
            </template>
        </i18n>

        <button
            v-if="isStatusToAdd && isRenter"
            :disabled="isLoading"
            class="button button--tertiary-contrast button--full-width"
            @click="$emit('click')"
        >
            {{ $t('common.add') }}
        </button>

        <p v-if="isStatusAdded && isRenter" class="omocom-optional-insurance__added">{{ $t('common.added') }}</p>

        <div
            v-if="(isOmocomCompleteInsurance || isOmocomPlusInsurance) && seeDetailsVisible"
            class="omocom-optional-insurance__see-details"
        >
            <a @click="seeDetails">
                {{ $t('common.see_details') }}
            </a>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { actionTypes as modalActions } from '~/store/modal';
import { actionTypes as slideUpModalActions } from '~/store/slideUpModal';
import { INSURANCE_ADDON, OMOCOM_BASIC, OMOCOM_COMPLETE, OMOCOM_PLUS } from '~/config/productTypes';

export default {
    name: 'OmocomOptionalInsurance',
    props: {
        productKey: {
            type: String,
            required: true,
        },
        product: {
            type: Object,
            default: null,
        },
        omocomCoverageProducts: {
            type: Array,
            default: null,
        },
        currentCoverageKey: {
            type: String,
            default: '',
        },
        status: {
            type: String,
            default: '',
            validator: value => ['', 'toAdd', 'added'].includes(value),
        },
        vehicleCountry: {
            type: String,
            default: '',
        },
        useSlideUpModal: {
            type: Boolean,
            default: false,
        },
        showOwnerInfo: {
            type: Boolean,
            default: false,
        },
        seeDetailsVisible: {
            type: Boolean,
            default: true,
        },
        isLoading: {
            type: Boolean,
            default: false,
        },
        isPopular: {
            type: Boolean,
            default: false,
        },
        useCheckbox: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            reduceDeductible: this.currentCoverageKey === OMOCOM_COMPLETE,
        };
    },
    computed: {
        ...mapGetters(['isLargeScreen', 'isRenter']),
        modifiers() {
            return {
                'omocom-optional-insurance--added': this.isStatusAdded,
                'omocom-optional-insurance--popular-choice': this.isPopular,
            };
        },
        isStatusAdded() {
            return this.status === 'added';
        },
        isStatusToAdd() {
            return this.status === 'toAdd';
        },
        omocomProductSubtrahendValue() {
            let product = this.omocomBasicCoverageProduct;

            if (this.currentCoverageKey && this.currentCoverageKey !== this.productKey) {
                product = this.omocomCoverageProducts.find(product => product.key === this.currentCoverageKey);
            }

            return product?.price;
        },
        omocomCompleteCoverageProduct() {
            return this.omocomCoverageProducts?.find(product => product.key === OMOCOM_COMPLETE);
        },
        omocomPlusCoverageProduct() {
            return this.omocomCoverageProducts?.find(product => product.key === OMOCOM_PLUS);
        },
        omocomBasicCoverageProduct() {
            return this.omocomCoverageProducts?.find(product => product.key === OMOCOM_BASIC);
        },
        omocomBasicCoverageProductDeductibleAmount() {
            if (!this.omocomBasicCoverageProduct) {
                return 0;
            }

            const amount = this.omocomBasicCoverageProduct.specifications?.deductibleAmount;

            return this.formattedPrice(amount, this.omocomBasicCoverageProduct.currency);
        },
        currentOmocomCoverageProductDeductibleAmount() {
            if (!this.currentOmocomCoverageProduct) {
                return 0;
            }

            const amount = this.currentOmocomCoverageProduct.specifications?.deductibleAmount;

            return this.formattedPrice(amount, this.currentOmocomCoverageProduct.currency);
        },
        currentOmocomCoverageProduct() {
            if (this.isOmocomCompleteInsurance) {
                return this.omocomCompleteCoverageProduct;
            }

            if (this.isOmocomPlusInsurance) {
                return this.omocomPlusCoverageProduct;
            }

            return null;
        },
        omocomCoverageProductPrice() {
            if (!this.currentOmocomCoverageProduct || !this.omocomProductSubtrahendValue || this.showOwnerInfo) {
                return 0;
            }

            const amountDiff = this.currentOmocomCoverageProduct.price - this.omocomProductSubtrahendValue;
            const formattedPrice = this.formattedPrice(amountDiff, this.currentOmocomCoverageProduct.currency);

            return `+ ${formattedPrice}`;
        },
        isOmocomCompleteInsurance() {
            return this.productKey === OMOCOM_COMPLETE;
        },
        isOmocomPlusInsurance() {
            return this.productKey === OMOCOM_PLUS;
        },
        productPrice() {
            if (!this.product || this.showOwnerInfo) {
                return 0;
            }

            return `+ ${this.formattedPrice(this.product.price, this.product.currency, 2)}`;
        },
        insuranceDetails() {
            if (this.isOmocomCompleteInsurance) {
                return {
                    title: this.$t('pages.booking_request.insurance_step.complete_reduce_deductible'),
                    infoText: this.$t('pages.booking_request.insurance_step.omocom_reduce_deductible_info', {
                        maxDeductible: this.omocomBasicCoverageProductDeductibleAmount,
                        reducedDeductible: this.currentOmocomCoverageProductDeductibleAmount,
                    }),
                    extraInfoText: this.showOwnerInfo
                        ? this.$t('pages.booking.booking_menu.complete_reduce_deductible_owner_info')
                        : this.$t('pages.booking_request.insurance_step.complete_reduce_deductible_info2'),
                    price: this.omocomCoverageProductPrice,
                };
            }

            if (this.isOmocomPlusInsurance) {
                return {
                    title: this.$t('pages.booking_request.insurance_step.plus_reduce_deductible'),
                    infoText: this.$t('pages.booking_request.insurance_step.omocom_reduce_deductible_info', {
                        maxDeductible: this.omocomBasicCoverageProductDeductibleAmount,
                        reducedDeductible: this.currentOmocomCoverageProductDeductibleAmount,
                    }),
                    price: this.omocomCoverageProductPrice,
                };
            }

            if (this.productKey === INSURANCE_ADDON.travel_abroad) {
                return {
                    title: this.showOwnerInfo
                        ? this.$t('pages.booking.booking_menu.travel_abroad_owner_title')
                        : this.$t('pages.vehicle.travel_abroad_title', {
                              country: this.$t(`localization.countries.${this.vehicleCountry}`),
                          }),
                    infoText: this.showOwnerInfo
                        ? this.$t('pages.booking.booking_menu.travel_abroad_owner_info', {
                              country: this.$t(`localization.countries.${this.vehicleCountry}`),
                          })
                        : this.$t('pages.vehicle.travel_abroad_insurance_info'),
                    price: this.productPrice,
                };
            }

            if (this.productKey === INSURANCE_ADDON.young_driver) {
                return {
                    title: this.$t('booking_addons.young_driver_title'),
                    infoText: this.showOwnerInfo
                        ? this.$t('pages.booking.booking_menu.young_driver_owner_info')
                        : this.$t('pages.vehicle.young_driver_insurance_info'),
                    price: this.productPrice,
                };
            }

            return '';
        },
    },
    methods: {
        onChange() {
            const oldKey = this.reduceDeductible ? OMOCOM_BASIC : OMOCOM_COMPLETE;
            const key = this.reduceDeductible ? OMOCOM_COMPLETE : OMOCOM_BASIC;

            this.$emit('change', { key, oldKey });
        },
        seeDetails() {
            const data = {
                product: this.currentOmocomCoverageProduct,
                omocomBasicProduct: this.omocomBasicCoverageProduct,
                showOwnerInfo: this.showOwnerInfo,
            };

            if (!this.isLargeScreen && this.useSlideUpModal) {
                this.displayOmocomInsuranceProtectionDetailsSlideUpModal(data);
            } else {
                this.displayOmocomInsuranceProtectionDetailsModal(data);
            }
        },
        async displayOmocomInsuranceProtectionDetailsModal(data) {
            try {
                return await this.$store.dispatch(modalActions.MODAL_OPEN, {
                    component: 'OmocomInsuranceProtectionDetailsModal',
                    style: { lesserPadding: true },
                    data,
                });
            } catch (err) {}
        },
        async displayOmocomInsuranceProtectionDetailsSlideUpModal(data) {
            try {
                return await this.$store.dispatch(slideUpModalActions.SLIDEUP_MODAL_OPEN, {
                    component: 'OmocomInsuranceProtectionDetailsModal',
                    data,
                });
            } catch (err) {}
        },
    },
};
</script>

<style scoped lang="scss">
@import '@/sass/_variables.scss';

.omocom-optional-insurance {
    @extend %card;
    position: relative;
    padding: 16px;

    .pill {
        position: absolute;
        top: -16px;
        left: 16px;
        @include font-size(12px);
        padding: 6px 12px;
    }

    &__wrapper {
        display: flex;
        justify-content: space-between;
    }

    .svg-icon {
        width: 24px;
        height: 24px;
        min-width: 24px;
        margin-left: 16px;
        color: $blue-dark;
    }

    &__price {
        @include font-size(14px);
        margin-top: 16px;

        strong {
            @include font-size(16px);
        }
    }

    &__see-details {
        margin-top: 24px;
        text-align: right;
        color: $primary;
    }

    &__title {
        margin-bottom: 16px;
        font-weight: 600;
    }

    &__description {
        @include font-size(14px);

        p:last-child {
            margin-bottom: 0;
        }
    }

    &__added {
        font-weight: 600;
        color: $darkest-gray;
        margin: 16px 0 0;
    }

    .button {
        margin-top: 16px;
    }

    &--popular-choice {
        padding-top: 32px;
    }

    &--added {
        background-color: $bright-blue-light;
        border: none;
    }

    &__checkbox-label {
        line-height: 26px;
    }
}
</style>
